/* FNDRY — PDP gallery + buy box
   Plain JS, no framework.

   Failure direction, decided up front: with this script absent or thrown, the
   gallery is the whole media set stacked (every image reachable), every
   accordion panel is open, and the form is a plain product form that posts to
   /cart/add and lands on the cart. Nothing here is load-bearing for reading or
   for buying — it only makes both nicer.

   Accordion behaviour is fndry-faq.js's, driven off the data-faq-* attributes
   in the markup. This file never touches it. */
(function () {
  'use strict';

  var ROOT = '[data-fndry-pdp]';

  function slice(list) {
    return Array.prototype.slice.call(list || []);
  }

  function money(node, text) {
    if (node && typeof text === 'string' && text) node.textContent = text;
  }

  /* A comparison figure and the node holding it come and go together: an empty
     <s> still draws its own gap, and a strikethrough with nothing struck off it
     is a price claim with no saving behind it. Both are already formatted in
     Liquid - nothing here builds a currency string. */
  function figure(node, text) {
    if (!node) return;
    if (text) {
      node.textContent = text;
      node.removeAttribute('hidden');
    } else {
      node.textContent = '';
      node.setAttribute('hidden', '');
    }
  }

  /* A Clarity custom event, when Clarity is on the page. Never throws. */
  function track(name) {
    try {
      if (window.clarity) window.clarity('event', name);
    } catch (error) {}
  }

  /* ------------------------------------------------------------------ */

  function Pdp(root) {
    this.root = root;
    this.gallery = root.querySelector('[data-fpb-gallery]');
    this.slides = slice(root.querySelectorAll('[data-fpb-slide]'));
    this.thumbs = slice(root.querySelectorAll('[data-fpb-thumb]'));
    this.form = root.querySelector('.fndry-pdp__form');
    this.atc = root.querySelector('[data-fpb-atc]');
    this.bar = root.querySelector('[data-fpb-bar]');
    this.live = root.querySelector('[data-fpb-live]');
    this.priceNode = root.querySelector('[data-fpb-price]');
    this.atcPriceNode = root.querySelector('[data-fpb-atc-price]');
    this.atcWasNode = root.querySelector('[data-fpb-atc-was]');
    this.barPriceNode = root.querySelector('[data-fpb-bar-price]');
    this.barWasNode = root.querySelector('[data-fpb-bar-was]');
    this.barSaveNode = root.querySelector('[data-fpb-bar-save]');
    this.barMetaNode = root.querySelector('[data-fpb-bar-meta]');
    this.compareNote = root.querySelector('[data-fpb-compare-note]');
    this.variantFields = slice(root.querySelectorAll('[data-fpb-variant]'));
    this.variantField = this.variantFields[0] || null;
    this.offers = slice(root.querySelectorAll('.fndry-pdp__offer'));
    this.planSet = root.querySelector('[data-fpb-planset]');
    this.oneTime = root.querySelector('[data-fpb-onetime]');
    /* P11 2026-10-22: the single unit the one-time line adds, when the
       product has one (Liquid renders the old switch otherwise). */
    this.oneTimeAdd = (this.oneTime && this.oneTime.getAttribute('data-fpb-onetime-add')) || '';
    this.onceRadio = null;
    this.addons = slice(root.querySelectorAll('[data-fpb-addon]'));
    this.express = root.querySelector('[data-fpb-express]');
    this.plans = slice(root.querySelectorAll('[data-fpb-plan]'));
    /* P16 2026-10-23: the One-time / Subscribe choice above the offers, where
       the section renders it. Its Subscribe radio carries the selected
       option's own plan id, swapped on every change of option. */
    this.ptype = root.querySelector('[data-fpb-ptype]');
    this.ptypeOnce = root.querySelector('[data-fpb-ptype-once]');
    this.ptypeSub = root.querySelector('[data-fpb-ptype-sub]');

    /* F2 2026-09-25: the terms at the button (renewal line, shipping fact)
       and the price line under the name. Every sentence arrives finished from
       Liquid; the script only chooses between them and compares cents. */
    this.terms = root.querySelector('[data-fpb-terms]');
    this.renewal = root.querySelector('[data-fpb-renewal]');
    this.shipNodes = slice(root.querySelectorAll('[data-fpb-ship]'));
    this.lineNode = root.querySelector('[data-fpb-line]');
    /* F2b 2026-09-28: the per-day figure on the price line's second line,
       and the separator that only shows beside it. */
    this.lineDayNode = root.querySelector('[data-fpb-line-day]');
    this.lineSepNode = root.querySelector('[data-fpb-line-sep]');
    this.qtyInput = root.querySelector('[data-fpb-qty-input]');

    /* P1 2026-10-12: the Buy now button beside the name, and its saving
       line, which mirrors the selected card's. */
    this.buyNow = root.querySelector('[data-fpb-buynow]');
    this.buyNowSave = root.querySelector('[data-fpb-buynow-save]');

    /* Delivery wording for the bar's meta line, authored in the schema. */
    this.repeatLabel = (this.bar && this.bar.dataset.repeatLabel) || '';
    this.onceLabel = (this.bar && this.bar.dataset.onceLabel) || '';

    this.prices = {};
    var priceScript = root.querySelector('[data-fpb-prices]');
    if (priceScript) {
      try {
        this.prices = JSON.parse(priceScript.textContent) || {};
      } catch (error) {
        this.prices = {};
      }
    }

    /* P10 2026-10-21: variant id -> the free-guide line property
       ({ name, value }), and the one hidden input that carries it. Both are
       only in the markup on a product with a guide option. */
    this.guides = {};
    this.guideProp = root.querySelector('[data-fpb-guide-prop]');
    var guideScript = root.querySelector('[data-fpb-guides]');
    if (guideScript) {
      try {
        this.guides = JSON.parse(guideScript.textContent) || {};
      } catch (error) {
        this.guides = {};
      }
    }

    this.startGallery();
    this.startSticky();
    this.startQuantity();
    this.startOptions();
    this.startCart();
    this.startAddons();
    this.startBar();
    this.startUrgency();
  }

  /* ---------- gallery ------------------------------------------------ */

  /* P16 smooth slide 2026-10-23: the slides sit side by side in one
     horizontal scroll-snap track (data-fpb-track, CSS). A finger moves the
     images itself - both in view mid-swipe, momentum, one image per fling -
     and a vertical drag over the gallery still scrolls the page, all native.
     Arrows, thumbnails and keys glide the track to the image: a short
     ease-out tween (GLIDE_*), instant under reduced motion. Which image is
     showing is read back from the track by an IntersectionObserver, so
     nothing here measures layout while the track scrolls. Replaces the old
     stacked stage, where the outgoing image vanished and the new one faded
     in on its own. */
  var GLIDE_MS = 420;
  var GLIDE_STEP_MS = 40;
  var GLIDE_MAX_MS = 600;

  Pdp.prototype.startGallery = function () {
    var self = this;
    this.track = this.root.querySelector('[data-fpb-track]');
    if (!this.gallery || !this.track || this.slides.length < 2) return;

    /* Only now does the stack become a track - before this attribute lands
       the CSS is still showing everything (or, with scripting on, the first
       image alone). */
    this.gallery.setAttribute('data-fpb-ready', 'true');
    /* P16 v2 gallery: `thumbs_window` marks the rail data-fpb-window. */
    this.rail = this.root.querySelector('[data-fpb-thumbs]');
    this.windowed = !!(this.rail && this.rail.hasAttribute('data-fpb-window'));
    this.custom = this.gallery.hasAttribute('data-fpb-custom');
    this.videos = this.slides.map(function (slide) { return slide.querySelector('video'); });
    this.ratios = {};
    this.mark(1, false, false);

    this.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        self.show(parseInt(thumb.dataset.index, 10), false, true);
      });
      thumb.addEventListener('keydown', function (event) {
        var last = self.thumbs.length;
        var current = parseInt(thumb.dataset.index, 10);
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current + 1 > last ? 1 : current + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current - 1 < 1 ? last : current - 1;
        else if (event.key === 'Home') next = 1;
        else if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        self.show(next, true, true);
      });
    });

    /* P5 2026-10-12: the faint previous / next chevrons on the stage. */
    slice(this.root.querySelectorAll('[data-fpb-arrow]')).forEach(function (arrow) {
      arrow.addEventListener('click', function () {
        self.step(arrow.getAttribute('data-fpb-arrow') === 'prev' ? -1 : 1);
      });
    });

    this.startTrack();
  };

  /* The track's own listeners. Everything is passive and nothing reads
     layout: the observer reports how much of each slide is in the track,
     and the image more than half in view is the one marked. */
  Pdp.prototype.startTrack = function () {
    var self = this;
    var track = this.track;
    var opts = { passive: true };

    if (window.IntersectionObserver) {
      this.trackObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.ratios[entry.target.dataset.index] = entry.isIntersecting ? entry.intersectionRatio : 0;
        });
        if (self.gliding) return;
        var best = self.mostVisible();
        if (best && best !== self.current) self.mark(best, false, true);
      }, { root: track, threshold: [0, 0.5, 1] });
      this.slides.forEach(function (slide) { self.trackObserver.observe(slide); });
    } else {
      /* No observer: the position, read once a frame while it moves. */
      var frame = 0;
      track.addEventListener('scroll', function () {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = 0;
          if (self.gliding) return;
          var n = Math.round(track.scrollLeft / (track.clientWidth || 1)) + 1;
          if (self.slides[n - 1] && n !== self.current) self.mark(n, false, true);
        });
      }, opts);
    }

    /* A finger on the track, or a sideways wheel (a trackpad swipe), during
       a glide: the glide lands on its image at once and hands over. */
    track.addEventListener('touchstart', function () {
      self.stopGlide();
      self.warmUp();
    }, opts);
    track.addEventListener('wheel', function (event) {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      self.stopGlide();
      self.warmUp();
    }, opts);

    /* The neighbours of the first image wait for the page's load event (or
       the first touch), so they never compete with the first image. */
    if (document.readyState === 'complete') this.warmUp();
    else window.addEventListener('load', function () { self.warmUp(); });
  };

  Pdp.prototype.mostVisible = function () {
    var best = 0;
    var most = 0.5;
    for (var i = 1; i <= this.slides.length; i++) {
      var r = this.ratios[i] || 0;
      if (r >= most) { most = r; best = i; }
    }
    return best;
  };

  /* One image along, wrapping at either end like the thumbnail keys. The
     thumbnail rail follows, without taking focus. */
  Pdp.prototype.step = function (delta) {
    var last = this.slides.length;
    if (last < 2) return;
    var next = (this.current || 1) + delta;
    if (next > last) next = 1;
    if (next < 1) next = last;
    this.show(next, false, true);
    var thumb = this.thumbs[next - 1];
    if (thumb) this.centre(thumb);
  };

  /* Show image `index`: glide the track there (or jump, with `glide` off or
     reduced motion), and mark it straight away. */
  Pdp.prototype.show = function (index, focus, glide) {
    if (!this.track || !this.slides[index - 1]) return;
    this.slideTo(index, glide);
    this.mark(index, focus, false);
  };

  Pdp.prototype.slideTo = function (index, glide) {
    var self = this;
    var track = this.track;
    var slide = this.slides[index - 1];
    /* A glide already under way is re-aimed from wherever it has got to,
       snapping still off, so a quick second tap never jerks. */
    if (this.glideFrame) window.cancelAnimationFrame(this.glideFrame);
    this.glideFrame = 0;
    /* Where the slide sits in the track's scroll, from boxes (exact to the
       fraction; offsetLeft rounds). Read on a click, never while scrolling. */
    var from = track.scrollLeft;
    var to = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + from;
    var dist = to - from;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.glideTo = to;
    if (Math.abs(dist) < 1 || !glide || reduce || !window.requestAnimationFrame) {
      if (Math.abs(dist) >= 1) track.scrollLeft = to;
      this.stopGlide();
      return;
    }

    /* Everything the glide passes is fetched now, so it never slides past
       an empty frame. */
    var lo = Math.min(index, this.current || 1);
    var hi = Math.max(index, this.current || 1);
    for (var k = lo; k <= hi; k++) this.eager(k);

    var steps = Math.max(1, Math.round(Math.abs(dist) / (track.clientWidth || 1)));
    var duration = Math.min(GLIDE_MS + GLIDE_STEP_MS * (steps - 1), GLIDE_MAX_MS);
    var start = window.performance && performance.now ? performance.now() : Date.now();
    this.gliding = true;
    /* Snapping is off for the glide (it would pull every frame back to the
       nearest image) and back on at the end, where the track sits exactly
       on an image. */
    track.style.scrollSnapType = 'none';
    function tick(now) {
      var p = Math.max(0, Math.min(1, (now - start) / duration));
      var eased = 1 - Math.pow(1 - p, 3);
      track.scrollLeft = from + dist * eased;
      if (p < 1) self.glideFrame = window.requestAnimationFrame(tick);
      else self.stopGlide();
    }
    this.glideFrame = window.requestAnimationFrame(tick);
  };

  /* Ends a glide: the track lands on the glide's image (at once, if the
     visitor cut it short) and snapping comes back on. */
  Pdp.prototype.stopGlide = function () {
    if (this.glideFrame) window.cancelAnimationFrame(this.glideFrame);
    this.glideFrame = 0;
    if (!this.gliding) return;
    this.gliding = false;
    var track = this.track;
    if (!track) return;
    if (typeof this.glideTo === 'number' && Math.abs(track.scrollLeft - this.glideTo) >= 0.5) track.scrollLeft = this.glideTo;
    track.style.scrollSnapType = '';
  };

  /* Fetch a lazy slide image now. */
  Pdp.prototype.eager = function (k) {
    var slide = this.slides[k - 1];
    var img = slide && slide.querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
  };

  /* The images either side of the one showing (wrapping, like the arrows),
     so the next swipe pulls in a loaded picture. */
  Pdp.prototype.warm = function (index) {
    var count = this.slides.length;
    var self = this;
    [index + 1, index - 1].forEach(function (n) {
      self.eager(n > count ? 1 : n < 1 ? count : n);
    });
  };

  Pdp.prototype.warmUp = function () {
    this.warmed = true;
    this.warm(this.current || 1);
  };

  /* Marks image `index` as the one showing: the slide, its thumbnail, the
     band, the rail. `follow` keeps the thumbnail in view on a rail without
     a window (swipes and arrows; a tapped thumbnail is already in view). */
  Pdp.prototype.mark = function (index, focus, follow) {
    var self = this;
    this.current = index;

    this.slides.forEach(function (slide, i) {
      var on = parseInt(slide.dataset.index, 10) === index;
      slide.classList.toggle('is-active', on);
      if (on) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      /* A video slid out of view stops, and its controls leave the tab
         order until it is back. */
      var video = self.videos && self.videos[i];
      if (video) {
        if (on) {
          video.removeAttribute('tabindex');
        } else {
          video.setAttribute('tabindex', '-1');
          if (!video.paused) video.pause();
        }
      }
    });

    if (this.warmed) this.warm(index);

    /* P16 v2 gallery 2026-10-23: with the custom gallery (theme-asset
       images, data-fpb-custom), the band laid over the image shows over the
       product's own photos only; the finished images carry their own
       headline there. */
    if (this.custom) {
      var shown = this.slides[index - 1];
      this.gallery.classList.toggle('is-asset-active', !!(shown && shown.classList.contains('fndry-pdp__slide--asset')));
    }

    this.thumbs.forEach(function (thumb) {
      var on = parseInt(thumb.dataset.index, 10) === index;
      thumb.classList.toggle('is-active', on);
      thumb.setAttribute('aria-selected', on ? 'true' : 'false');
      thumb.setAttribute('tabindex', on ? '0' : '-1');
      if (on && focus) {
        /* preventScroll, then centre the rail by hand — the browser's own
           scroll-into-view yanks the page vertically. */
        thumb.focus({ preventScroll: true });
        self.centre(thumb);
      }
    });

    /* P16 v2 gallery: a windowed rail follows the image shown. */
    if (this.windowed) {
      this.follow(index);
    } else if (follow) {
      var active = this.thumbs[index - 1];
      if (active) this.centre(active);
    }
  };

  Pdp.prototype.centre = function (thumb) {
    if (this.windowed) {
      this.follow(parseInt(thumb.dataset.index, 10) || this.current || 1);
      return;
    }
    var rail = this.root.querySelector('[data-fpb-thumbs]');
    if (!rail || rail.scrollWidth <= rail.clientWidth) return;
    var railBox = rail.getBoundingClientRect();
    var thumbBox = thumb.getBoundingClientRect();
    var delta = thumbBox.left - railBox.left - (railBox.width - thumbBox.width) / 2;
    var left = Math.max(0, Math.min(rail.scrollLeft + delta, rail.scrollWidth - rail.clientWidth));
    rail.scrollLeft = left;
  };

  /* P16 v2 gallery 2026-10-23: `thumbs_window` - the rail shows a few
     thumbnails at a time. Whenever the image changes (swipe, arrow,
     thumbnail, keys) the rail scrolls so the active thumbnail sits second
     from the left: the one before it still in view, the next ones after it,
     held at both ends. Smooth, unless the visitor asks for reduced motion.
     Scrolling inside the rail moves nothing else on the page. */
  Pdp.prototype.follow = function (index) {
    var rail = this.rail;
    if (!rail || !this.thumbs.length) return;
    var max = rail.scrollWidth - rail.clientWidth;
    if (max <= 0) return;
    var first = this.thumbs[Math.max(0, Math.min(index - 2, this.thumbs.length - 1))];
    var left = Math.max(0, Math.min(first.offsetLeft - this.thumbs[0].offsetLeft, max));
    if (Math.abs(rail.scrollLeft - left) < 1) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof rail.scrollTo === 'function') rail.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
    else rail.scrollLeft = left;
  };

  /* ---------- sticky gallery (desktop) ------------------------------- */

  /* F2b 2026-09-28 (CRO D10): from 990px the gallery is sticky beside the
     long buy box (CSS). Where the whole gallery - stage plus thumbnails - is
     taller than the room under the header, it is lifted by the difference,
     so it sticks with its thumbnails on screen instead of hanging below the
     fold. Only a custom property is written; below 990px nothing reads it. */
  /* P15 2026-10-23: below 990px nothing reads the lift, so nothing is measured
     or written there - on phones this ran on every toolbar resize. Above it,
     the work waits for the next frame, and the lift is found without first
     resetting it to 0px (that reset cost a style recalc and a forced layout
     each time): the computed top already has the current lift taken off. */
  Pdp.prototype.startSticky = function () {
    var gallery = this.gallery;
    if (!gallery || !window.getComputedStyle) return;
    var wide = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
    var lift = 0;
    var frame = 0;

    function place() {
      frame = 0;
      if (wide && !wide.matches) {
        if (lift !== 0) {
          lift = 0;
          gallery.style.setProperty('--fpb-gallery-lift', '0px');
        }
        return;
      }
      var top = (parseFloat(window.getComputedStyle(gallery).top) || 0) + lift;
      var over = Math.max(0, Math.round(gallery.offsetHeight + top + 24 - window.innerHeight));
      if (over !== lift) {
        lift = over;
        gallery.style.setProperty('--fpb-gallery-lift', over + 'px');
      }
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(place);
    }

    place();
    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) {
      this.stickyObserver = new ResizeObserver(schedule);
      this.stickyObserver.observe(gallery);
    }
    this.placeSticky = schedule;
  };

  /* ---------- quantity ----------------------------------------------- */

  Pdp.prototype.startQuantity = function () {
    var self = this;
    var input = this.root.querySelector('[data-fpb-qty-input]');
    if (!input) return;
    slice(this.root.querySelectorAll('[data-fpb-qty]')).forEach(function (button) {
      button.addEventListener('click', function () {
        var step = parseInt(button.dataset.fpbQty, 10) || 0;
        var next = (parseInt(input.value, 10) || 1) + step;
        input.value = String(Math.max(1, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    /* The quantity moves the one-time total across the free-shipping line and
       turns the renewal price into a per-unit one, so the terms follow it. */
    input.addEventListener('change', function () { self.syncTerms(); });
    input.addEventListener('input', function () { self.syncTerms(); });
  };

  Pdp.prototype.quantity = function () {
    var q = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
    return q > 0 ? q : 1;
  };

  /* The terms at the button and the price line under the name, for whatever
     is selected now. The renewal HTML is built and escaped in Liquid from the
     plan's own interval and price (the only markup in it is the link to the
     subscription terms), so it is set as HTML; everything else is text. */
  Pdp.prototype.syncTerms = function () {
    var entry = this.prices[this.currentVariantId()];
    if (!entry) return;
    var planId = this.currentPlanId();
    var plan = planId && entry.plans ? entry.plans[planId] || null : null;
    var qty = this.quantity();

    if (this.lineNode && typeof entry.line === 'string' && entry.line) {
      this.lineNode.textContent = entry.line;
    }

    /* The selected option's per-day figure: the plan's, or the one-time one.
       A plan that renews before the pack runs out carries none (see
       snippets/fndry-per-day.liquid), and then the separator goes too. */
    if (this.lineDayNode) {
      var dayText = (plan ? plan.day : entry.baseDay) || '';
      figure(this.lineDayNode, dayText);
      if (this.lineSepNode) {
        if (dayText) this.lineSepNode.removeAttribute('hidden');
        else this.lineSepNode.setAttribute('hidden', '');
      }
    }

    if (this.renewal && this.terms) {
      var once = this.terms.dataset.once || '';
      if (plan && (plan.renew || plan.renewEach)) {
        this.renewal.innerHTML = qty > 1 ? plan.renewEach : plan.renew;
        this.renewal.removeAttribute('hidden');
      } else if (once) {
        this.renewal.textContent = once;
        this.renewal.removeAttribute('hidden');
      } else {
        this.renewal.setAttribute('hidden', '');
      }
    }

    if (this.terms && this.shipNodes.length) {
      var d = this.terms.dataset;
      var freeCents = parseInt(d.freeCents, 10) || 0;
      var free = false;
      if (d.allFree === 'true') {
        free = true;
      } else if (plan) {
        if (d.subsFree === 'true') free = true;
        else if (freeCents > 0 && (plan.cents || 0) * qty >= freeCents) free = true;
      } else if (freeCents > 0 && (entry.baseCents || 0) * qty >= freeCents) {
        free = true;
      }
      var text = free ? d.shipFree : d.shipOver;
      if (text) {
        this.shipNodes.forEach(function (node) { node.textContent = text; });
      }
    }
  };

  /* ---------- price, variant, plan ------------------------------------ */

  Pdp.prototype.currentVariantId = function () {
    for (var i = 0; i < this.variantFields.length; i++) {
      var f = this.variantFields[i];
      if (f.type === 'radio') {
        if (f.checked) return f.value;
      } else if (f.value) {
        return f.value;
      }
    }
    return null;
  };

  Pdp.prototype.currentPlanId = function () {
    for (var i = 0; i < this.plans.length; i++) {
      if (this.plans[i].checked) return this.plans[i].value || '';
    }
    return '';
  };

  Pdp.prototype.syncPrice = function () {
    var variantId = this.currentVariantId();
    if (!variantId) return;
    var entry = this.prices[variantId];
    if (!entry) return;

    /* An id with no allocation on this variant is not an error - it is a plan
       this variant doesn't carry - so it falls back to the one-time price,
       which is what syncOptions is about to select anyway. */
    var plan = null;
    var planId = this.currentPlanId();
    if (planId && entry.plans) plan = entry.plans[planId] || null;

    var text = plan ? plan.price : entry.base;
    var save = plan ? plan.save : entry.baseSave;
    /* P16 v2 2026-10-23: on one-time with the strike on, the button and the
       bar strike what the selected card strikes. */
    var was = entry.was;
    var strike = !!(this.ptype && !plan && entry.onceStrikeWas);
    if (strike) {
      was = entry.onceStrikeWas;
      save = entry.onceStrikeAmt;
    }

    money(this.priceNode, text);
    money(this.atcPriceNode, text);
    money(this.barPriceNode, text);

    /* On the button, the comparison is the two totals and nothing else - no
       "Save" wording competing with the label it sits inside. */
    figure(this.atcWasNode, save ? was : '');

    /* `save` is null wherever this option already sits at the baseline - a
       one-time price on a product that was never marked down - and then there
       is nothing to strike through either. */
    figure(this.barWasNode, save ? was : '');
    figure(this.barSaveNode, save || '');

    if (this.barMetaNode) {
      var parts = [];
      if (entry.title && entry.title !== 'Default Title') parts.push(entry.title);
      var mode = plan ? this.repeatLabel : this.onceLabel;
      if (mode) parts.push(mode);
      this.barMetaNode.textContent = parts.join(' · ');
    }

    /* The line explaining the struck-through price is only true where the
       baseline came from compare_at_price, and one product can carry it on
       some variants and not others. */
    if (this.compareNote) {
      if (entry.wasCompare && !strike) this.compareNote.removeAttribute('hidden');
      else this.compareNote.setAttribute('hidden', '');
    }

    this.syncTerms();
    this.syncBuyNow();
  };

  /* P1 2026-10-12: Buy now's "Save $X" is whatever the selected card's own
     saving line says at this moment - copied, never recomputed - so the two
     cannot show different numbers. syncPrice runs after syncOffers on every
     path (offer, delivery choice, load), so the card is already current. */
  Pdp.prototype.syncBuyNow = function () {
    if (!this.buyNowSave) return;
    var text = '';
    for (var i = 0; i < this.offers.length; i++) {
      var input = this.offers[i].querySelector('input');
      if (input && input.checked) {
        var node = this.offers[i].querySelector('[data-fpb-offer-save]');
        if (node && !node.hasAttribute('hidden')) text = (node.textContent || '').trim();
        break;
      }
    }
    figure(this.buyNowSave, text);
  };

  /* The offer rows quote a monthly price, and it has to move with the delivery
     choice the way the button does - otherwise the row says one thing and the
     button charges another, which is the complaint this replaced.

     Each row reads its OWN variant, not the selected one: with recurring
     chosen, every row shows its own plan's monthly rate, so the ladder down
     the stack is the real one. Reading the checked plan id would be wrong here
     - that id belongs to whichever variant is selected and is not allocated to
     the others. */
  Pdp.prototype.syncOffers = function () {
    var self = this;
    var onceChosen = this.currentPlanId() === '';

    this.offers.forEach(function (card) {
      var input = card.querySelector('input');
      var entry = input ? self.prices[input.value] : null;
      if (!entry) return;

      var priceNode = card.querySelector('[data-fpb-offer-price]');
      var wasNode = card.querySelector('[data-fpb-offer-was]');
      var saveNode = card.querySelector('[data-fpb-offer-save]');
      var dayNode = card.querySelector('[data-fpb-offer-day]');

      var plan = null;
      if (!onceChosen && entry.subPlanId && entry.plans) plan = entry.plans[entry.subPlanId] || null;

      /* P16: with the purchase choice on, one-time shows each option's own
         total - no per-month division, no suffix - and a saving only where
         the option carries a compare-at price. */
      if (self.ptype && !plan) {
        money(priceNode, entry.base);
        /* P16 v2 2026-10-23: the one-time strike, where the section sets
           one (per unit, display only); otherwise the compare-at saving. */
        if (entry.onceStrikeWas) {
          figure(wasNode, entry.onceStrikeWas);
          figure(saveNode, entry.onceStrikeSave || '');
        } else {
          figure(wasNode, entry.onceSave ? entry.was : '');
          figure(saveNode, entry.onceSave || '');
        }
        figure(dayNode, entry.baseDay || '');
        return;
      }

      var month = plan ? plan.month : entry.baseMonth;
      var save = plan ? plan.monthSave : entry.baseMonthSave;

      money(priceNode, month);
      figure(wasNode, save ? entry.monthWas : '');
      figure(saveNode, save || '');
      /* CRO D7: the per-day figure follows the delivery choice like the price
         above it - the subscription's per-day, or the one-time per-day. F2b:
         a plan with no honest daily figure hides the line rather than keep
         the last one. */
      figure(dayNode, (plan ? plan.day : entry.baseDay) || '');
    });
  };

  /* A plan is only offered on the variants it is actually allocated to. Liquid
     renders the list for the variant showing on load; this re-resolves it for
     whichever variant is showing now. Hiding an unavailable plan beats printing
     a fallback price the customer would never be charged. */
  Pdp.prototype.syncOptions = function () {
    var entry = this.prices[this.currentVariantId()] || {};
    if (this.ptypeSub) this.syncPtype(entry);
    var planPrices = entry.plans || {};
    var lostTheChecked = false;

    slice(this.root.querySelectorAll('[data-fpb-option]')).forEach(function (card) {
      var planId = card.getAttribute('data-plan') || '';
      var input = card.querySelector('input');
      var priceNode = card.querySelector('[data-fpb-option-price]');
      var wasNode = card.querySelector('[data-fpb-option-was]');
      var saveNode = card.querySelector('[data-fpb-option-save]');
      var plan = planId === '' ? null : planPrices[planId] || null;
      var price = planId === '' ? entry.base : plan && plan.price;
      var save = planId === '' ? entry.baseSave : plan && plan.save;

      if (price) {
        card.removeAttribute('hidden');
        input.removeAttribute('disabled');
        money(priceNode, price);
        figure(wasNode, save ? entry.was : '');
        figure(saveNode, save || '');
        return;
      }
      if (input.checked) lostTheChecked = true;
      input.checked = false;
      input.setAttribute('disabled', 'disabled');
      card.setAttribute('hidden', '');
    });

    if (lostTheChecked) {
      /* Never leave the group with nothing selected — fall back to whatever is
         still offered, which is the one-time purchase in every real case. */
      var fallback = this.root.querySelector('[data-fpb-option]:not([hidden]) input');
      if (fallback) fallback.checked = true;
    }

    /* P11: where the one-time line adds its own unit, nothing on screen can
       put the box on one-time any more, so it only sits there while the
       selected option has no plan (Astaxanthin's 2 and 3 Pack). An option
       that has one moves the box back onto it. */
    if (this.onceRadio && this.onceRadio.checked) {
      var planInput = this.root.querySelector('[data-fpb-option]:not([hidden]) input[value]:not([value=""])');
      if (planInput) planInput.checked = true;
    }
    this.markSelected();
  };

  /* P16: Subscribe always carries the selected option's own plan. An option
     with no plan can only be bought one-time, so Subscribe is switched off
     there and the choice falls back to one-time. */
  Pdp.prototype.syncPtype = function (entry) {
    var sub = this.ptypeSub;
    var label = sub.closest('.fndry-pdp__ptype-opt');
    var id = entry && entry.subPlanId ? String(entry.subPlanId) : '';
    if (id) {
      sub.value = id;
      sub.disabled = false;
      if (label) label.classList.remove('is-disabled');
      return;
    }
    if (sub.checked && this.ptypeOnce) this.ptypeOnce.checked = true;
    sub.checked = false;
    sub.disabled = true;
    if (label) label.classList.add('is-disabled');
  };

  Pdp.prototype.startOptions = function () {
    var self = this;

    this.plans.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (self.ptype) track(radio.value ? 'pdp_ptype_sub' : 'pdp_ptype_once');
        self.markSelected();
        self.syncOffers();
        self.syncPrice();
      });
    });

    this.variantFields.forEach(function (f) {
      if (f.type === 'hidden') return;
      f.addEventListener('change', function () {
        self.markOffers();
        self.syncOptions();
        self.syncOffers();
        self.syncPrice();
        self.syncGuide();
      });
    });

    this.collapsePlan();
    this.markOffers();

    this.syncOptions();
    this.syncOffers();
    this.syncPrice();
    this.syncGuide();
  };

  /* P10 2026-10-21: the free-guide property follows the selected option. On
     an option with a guide the input is named for it ("Free guide" or "Free
     guides") and enabled; on any other it is disabled, so FormData and a
     plain post both leave it out and the line carries no property. */
  Pdp.prototype.syncGuide = function () {
    var input = this.guideProp;
    if (!input) return;
    var entry = this.guides[this.currentVariantId()] || null;
    if (entry && entry.name && entry.value) {
      input.name = 'properties[' + entry.name + ']';
      input.value = entry.value;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  };

  /* :has() draws the selected card on its own; this class is the fallback for
     engines without it, and it costs one line. */
  /* :has() draws the chosen offer on its own; this is the fallback for
     engines without it. */
  Pdp.prototype.markOffers = function () {
    this.offers.forEach(function (card) {
      var input = card.querySelector('input');
      card.classList.toggle('is-selected', !!(input && input.checked));
    });
  };

  /* The delivery pair ships visible so it works with no JS. Once we are
     running, recurring is the silent default and the only visible control
     is the one-time line: since P11 a direct one-time add of the single
     unit, and the old two-way switch only where the product has none. */
  Pdp.prototype.collapsePlan = function () {
    var self = this;
    if (!this.planSet || !this.oneTime) return;

    var recurring = null;
    var once = null;
    this.plans.forEach(function (radio) {
      if (radio.value) { if (!recurring) recurring = radio; } else { once = radio; }
    });
    if (!recurring || !once) return;

    this.planSet.setAttribute('hidden', '');
    this.oneTime.removeAttribute('hidden');

    /* P11 2026-10-22: a direct add, not a switch. The box stays on repeat
       delivery - even over a one-time choice the browser restored from an
       earlier visit - and the line adds its own one-time unit. */
    if (this.oneTimeAdd) {
      this.onceRadio = once;
      recurring.checked = true;
      this.oneTime.addEventListener('click', function () {
        self.addOnce();
      });
      window.addEventListener('pageshow', function (event) {
        if (!event.persisted) return;
        self.onceBusy = false;
        self.oneTime.removeAttribute('aria-busy');
      });
      return;
    }

    if (!once.checked) recurring.checked = true;

    var label = this.oneTime.querySelector('[data-fpb-onetime-label]');
    var onceText = this.oneTime.dataset.onceText || (label ? label.textContent : 'One-time purchase');
    var backText = this.oneTime.dataset.backText || 'Switch back to repeat delivery';

    function paint() {
      if (label) label.textContent = once.checked ? backText : onceText;
      self.oneTime.setAttribute('aria-pressed', once.checked ? 'true' : 'false');
    }

    this.oneTime.addEventListener('click', function () {
      if (once.checked) recurring.checked = true; else once.checked = true;
      paint();
      self.syncOffers();
      self.syncPrice();
    });
    paint();
  };

  /* P11 2026-10-22: the one-time line's own add - one of the single unit,
     no selling plan, no line property. The body is built here and never read
     from the form, so nothing the box has selected (its plan, its option, the
     P10 guide note) can ride along. Then the same cart event Add to cart
     fires, so the drawer opens and every quick-add re-derives its state. */
  Pdp.prototype.addOnce = function () {
    var self = this;
    var btn = this.oneTime;
    var id = this.oneTimeAdd;
    if (!btn || !id || this.onceBusy) return;
    this.onceBusy = true;
    btn.setAttribute('aria-busy', 'true');

    var addUrl = this.root.dataset.cartAddUrl || '/cart/add';
    var added = false;
    var body = new FormData();
    body.append('id', id);
    body.append('quantity', '1');

    function release() {
      self.onceBusy = false;
      btn.removeAttribute('aria-busy');
    }

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (response) {
        if (!response.ok) throw new Error('one-time add failed');
        return response.json();
      })
      .then(function (item) {
        added = true;
        track('pdp_onetime_add');
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('fndry:cart:updated', {
                detail: { cart: cart, variantId: item.variant_id, source: 'fndry-pdp-buy-once' }
              })
            );
            if (self.live) self.live.textContent = (self.atc && self.atc.dataset.addedLabel) || 'Added';
            window.setTimeout(function () {
              release();
              if (self.live) self.live.textContent = '';
            }, 1200);
          });
      })
      .catch(function () {
        release();
        /* The unit is in the cart and only the read-back failed: say so to
           the cart, never add it twice. */
        if (added) {
          document.dispatchEvent(new CustomEvent('fndry:cart:refresh'));
          return;
        }
        /* Otherwise she still gets it: a plain post of the same two fields,
           which lands on the cart the normal way. */
        self.postOnce(addUrl, id);
      });
  };

  Pdp.prototype.postOnce = function (addUrl, id) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = addUrl;
    form.setAttribute('hidden', '');
    [['form_type', 'product'], ['utf8', '✓'], ['id', id], ['quantity', '1']].forEach(function (pair) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = pair[0];
      input.value = pair[1];
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  /* Add-ons post their own line and fire the same cart event, so the
     drawer opens and every quick-add on the page re-derives its state. */
  Pdp.prototype.startAddons = function () {
    var self = this;
    this.addons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.hasAttribute('disabled')) return;
        btn.setAttribute('disabled', 'disabled');
        var body = new FormData();
        body.append('id', btn.dataset.variantId);
        body.append('quantity', '1');
        fetch((self.root.dataset.cartAddUrl || '/cart/add') + '.js', {
          method: 'POST', headers: { Accept: 'application/json' }, body: body
        })
          .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
          .then(function (item) {
            return fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                document.dispatchEvent(new CustomEvent('fndry:cart:updated', {
                  detail: { cart: cart, variantId: item.variant_id, source: 'fndry-pdp-buy-addon' }
                }));
                var label = btn.querySelector('[data-fpb-addon-label]');
                if (label) label.textContent = btn.dataset.addedLabel || 'Added';
              });
          })
          .catch(function () { btn.removeAttribute('disabled'); });
      });
    });
  };

  Pdp.prototype.markSelected = function () {
    this.plans.forEach(function (radio) {
      var card = radio.closest('.fndry-pdp__option, .fndry-pdp__ptype-opt');
      if (card) card.classList.toggle('is-selected', radio.checked);
    });
  };

  /* ---------- add to cart --------------------------------------------- */

  Pdp.prototype.startCart = function () {
    var self = this;
    if (!this.form || !this.atc) return;

    /* Which button submitted decides where we land. Captured on the way down
       so it is set before the submit handler runs. */
    if (this.express) {
      this.express.addEventListener('click', function () { self.toCheckout = true; }, true);
    }
    /* P1: Buy now sits outside the form (form="…") and takes the same path
       as the express button: the form's own add, then /checkout. */
    if (this.buyNow) {
      this.buyNow.addEventListener('click', function () { self.toCheckout = true; }, true);
    }
    if (this.atc) {
      this.atc.addEventListener('click', function () { self.toCheckout = false; }, true);
    }

    /* Back from checkout through the browser's page cache: the buttons were
       left disabled by the redirect, so hand them back. */
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      self.busy = false;
      self.toCheckout = false;
      self.atc.removeAttribute('disabled');
      if (self.express) self.express.removeAttribute('disabled');
      if (self.buyNow) self.buyNow.removeAttribute('disabled');
    });

    this.form.addEventListener('submit', function (event) {
      if (self.busy) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      self.add(self.toCheckout === true);
      self.toCheckout = false;
    });
  };

  Pdp.prototype.add = function (goToCheckout) {
    var self = this;
    var addUrl = this.root.dataset.cartAddUrl || '/cart/add';
    /* P10: read the selection once more right before the form is read, so
       the guide property can never belong to a previous option. */
    this.syncGuide();
    var data = new FormData(this.form);
    /* P16: a one-time purchase sends no selling plan at all. */
    if (data.get && data.get('selling_plan') === '') data.delete('selling_plan');

    this.busy = true;
    this.atc.setAttribute('disabled', 'disabled');
    if (this.express) this.express.setAttribute('disabled', 'disabled');
    if (this.buyNow) this.buyNow.setAttribute('disabled', 'disabled');

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add-to-cart failed');
        return response.json();
      })
      .then(function (item) {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            /* The header and the cart drawer both listen for this; the drawer
               opens itself off the variantId in the payload. */
            if (goToCheckout) {
              window.location.href = '/checkout';
              return;
            }
            document.dispatchEvent(
              new CustomEvent('fndry:cart:updated', {
                detail: { cart: cart, variantId: item.variant_id, source: 'fndry-pdp-buy' }
              })
            );
            self.confirm();
          });
      })
      .catch(function () {
        /* Whatever went wrong, the customer still gets to buy: hand the form
           back to the browser and let it post the normal way. */
        self.busy = false;
        self.atc.removeAttribute('disabled');
        if (self.express) self.express.removeAttribute('disabled');
        if (self.buyNow) self.buyNow.removeAttribute('disabled');
        self.form.submit();
      });
  };

  Pdp.prototype.confirm = function () {
    var self = this;
    var added = this.atc.dataset.addedLabel || 'Added';
    this.atc.classList.add('is-added');
    if (this.live) this.live.textContent = added;

    window.clearTimeout(this.resetTimer);
    this.resetTimer = window.setTimeout(function () {
      self.atc.classList.remove('is-added');
      self.atc.removeAttribute('disabled');
      if (self.express) self.express.removeAttribute('disabled');
      if (self.buyNow) self.buyNow.removeAttribute('disabled');
      self.busy = false;
      if (self.live) self.live.textContent = '';
    }, 2200);
  };

  /* ---------- sticky mobile bar --------------------------------------- */

  Pdp.prototype.startBar = function () {
    var self = this;
    if (!this.bar || !this.atc || !window.IntersectionObserver) return;

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* Show the bar once the real button has left upward, never while it
             is still below the fold on first paint. */
          var gone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          if (gone) self.bar.removeAttribute('hidden');
          else self.bar.setAttribute('hidden', '');
        });
      },
      /* QA 2026-09-25: the observer only reports a crossing of the viewport
         edge, and a 54px button can be skipped entirely by a fast fling or a
         jump (top to bottom, back-to-top, an anchor), which left the bar
         missing below the button or stranded over the top of the page. The
         bottom margin stretches the observed area far below the fold, so
         "below the screen" and "on screen" count the same and the only
         state change left is the one that matters: above the top, or not. */
      { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
    );
    this.observer.observe(this.atc);
  };

  /* ---------- shipping cut-off countdown (P1 2026-10-12) -------------- */

  /* Daily dispatch, including Saturday and Sunday (Nema, 2026-10-24).
     Before the 2pm Eastern cut-off: ships today. After it: ships tomorrow.
     Show the countdown/bar only in the configured final hours of today's
     dispatch window; overnight and after the cut-off use the plain ship-day
     line. Arrival estimates still count carrier working days separately.
     Intl keeps the dispatch day independent of the visitor's time zone. */
  var URGENCY_TZ = 'America/New_York';
  var URGENCY_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var URGENCY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  Pdp.prototype.startUrgency = function () {
    var box = this.root.querySelector('[data-fpb-urgency]');
    if (!box || !window.Intl || !Intl.DateTimeFormat) return;
    var textNode = box.querySelector('[data-fpb-urgency-text]');
    var fill = box.querySelector('[data-fpb-urgency-fill]');
    var arrivalNode = box.querySelector('[data-fpb-urgency-arrival]');
    var d = box.dataset;
    var cutoff = parseInt(d.cutoff, 10);
    if (!textNode || isNaN(cutoff) || !d.text) return;

    var windowHours = parseFloat(d.window);
    var windowMs = windowHours > 0 ? windowHours * 3600000 : 0;
    var arrivalOn = !!(arrivalNode && windowMs);
    var minDays = parseInt(d.arrivalMin, 10);
    var maxDays = parseInt(d.arrivalMax, 10);
    if (!(minDays >= 0)) minDays = 3;
    if (!(maxDays >= 0)) maxDays = 5;
    if (minDays > maxDays) {
      var swap = minDays;
      minDays = maxDays;
      maxDays = swap;
    }
    var suffix = d.arrivalSuffix || '';
    /* The editor can trim the setting's leading space. */
    if (suffix && !/^[\s,.;:]/.test(suffix)) suffix = ' ' + suffix;
    var holidays = {};
    (d.holidays || '').replace(/(\d{4})-(\d{1,2})-(\d{1,2})/g, function (all, y, m, dd) {
      holidays[Date.UTC(+y, +m - 1, +dd)] = true;
      return all;
    });

    var fmt;
    try {
      fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: URGENCY_TZ, hourCycle: 'h23',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric'
      });
      if (!fmt.formatToParts) return;
    } catch (error) {
      return;
    }

    /* Eastern wall-clock time as a UTC timestamp, so date arithmetic can run
       on getUTC* without the visitor's own zone getting involved. */
    function wall(ms) {
      var p = {};
      fmt.formatToParts(new Date(ms)).forEach(function (x) { p[x.type] = x.value; });
      return Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    }
    function cutOn(w, offsetDays) {
      var x = new Date(w);
      return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate() + offsetDays, cutoff, 0, 0);
    }
    function workday(w) {
      var g = new Date(w).getUTCDay();
      return g >= 1 && g <= 5;
    }
    function sameDate(a, b) {
      var x = new Date(a), y = new Date(b);
      return x.getUTCFullYear() === y.getUTCFullYear() && x.getUTCMonth() === y.getUTCMonth() && x.getUTCDate() === y.getUTCDate();
    }
    function span(mins) {
      if (mins >= 1440) return Math.floor(mins / 1440) + 'd ' + Math.floor((mins % 1440) / 60) + 'h';
      var h = Math.floor(mins / 60);
      var m = mins % 60;
      return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
    }
    function midnight(w) {
      var x = new Date(w);
      return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
    }
    /* The n-th working day after the ship day (1 = the first one after it). */
    function deliveryDay(ship, n) {
      var t = midnight(ship);
      for (var guard = 0; n > 0 && guard < 120; guard++) {
        t += 86400000;
        if (workday(t) && !holidays[t]) n--;
      }
      return t;
    }
    /* No-break spaces inside a date and before the dash, so a narrow card
       wraps after the dash or before "arrives", never inside "Tue 3 Nov". */
    function dateLabel(t, withMonth) {
      var x = new Date(t);
      return URGENCY_DAYS[x.getUTCDay()] + '\u00a0' + x.getUTCDate() + (withMonth ? '\u00a0' + URGENCY_MONTHS[x.getUTCMonth()] : '');
    }
    /* "Mon 2 – Wed 4 Nov", or "Fri 30 Oct – Tue 3 Nov" across a month. */
    function arrivalRange(ship) {
      var a = deliveryDay(ship, minDays);
      var b = deliveryDay(ship, maxDays);
      if (a === b) return dateLabel(b, true);
      var x = new Date(a);
      var y = new Date(b);
      var oneMonth = x.getUTCFullYear() === y.getUTCFullYear() && x.getUTCMonth() === y.getUTCMonth();
      return dateLabel(a, !oneMonth) + '\u00a0\u2013 ' + dateLabel(b, true);
    }
    function put(node, text) {
      if (node.textContent !== text) node.textContent = text;
    }

    function tick() {
      var now = Date.now();
      var w = wall(now);
      var next = cutOn(w, 0);
      var prev = cutOn(w, -1);
      if (next <= w) {
        prev = next;
        next = cutOn(w, 1);
      }

      /* Real milliseconds to the cut-off, corrected for a clock change
         falling between now and then (it only ever can over a weekend). */
      var target = now + (next - w);
      target -= wall(target) - next;
      var left = Math.max(0, target - now);
      var mins = Math.max(1, Math.ceil(left / 60000));

      var day;
      if (sameDate(next, w)) day = d.today || 'today';
      else day = d.tomorrow || 'tomorrow';

      /* P15 2026-10-23: write only what changed. The text moves once a minute
         and the bar by a fraction of a pixel per tick; rewriting both every
         15 seconds restarted the bar's width transition (a layout every frame
         for 0.6s) for no visible change. */
      var text = d.text.replace('{time}', span(mins)).replace('{day}', day);
      /* Countdown urgency belongs only to the current dispatch day. */
      var mode = 'weekend';
      if (arrivalOn) {
        mode = (!sameDate(next, w) || left > windowMs) && d.arrivalText ? 'arrival' : 'countdown';
      }
      if (mode === 'weekend') {
        put(textNode, text);
        if (arrivalNode) put(arrivalNode, '');
      } else {
        var range = arrivalRange(next);
        var tail = suffix.replace('{range}', range);
        if (mode === 'arrival') {
          /* Hidden in place under the arrival line: the countdown as it will
             read when the window opens, so the card already has its height. */
          var openDay = sameDate(next, next - windowMs) ? (d.today || 'today')
            : (d.tomorrow || 'tomorrow');
          put(textNode, d.text.replace('{time}', span(Math.round(windowMs / 60000))).replace('{day}', openDay) + tail);
        } else {
          put(textNode, text + tail);
        }
        put(arrivalNode, (d.arrivalText || '').replace('{day}', day).replace('{range}', range));
      }
      /* Same frame as the text, so the switch is one layout. */
      if (box.getAttribute('data-mode') !== mode) box.setAttribute('data-mode', mode);
      /* The bar is the last 24 hours before the cut-off, filling: near
         empty right after a cut-off, full at the next daily cut-off.
         Where the owner has set "% sold" (data-sold, from the product's
         custom.sold_percent) the one bar shows that figure instead, as
         Liquid drew it, and the script leaves it alone. */
      if (fill && !box.hasAttribute('data-sold')) {
        var share = Math.max(0, Math.min(1, left / Math.min(86400000, Math.max(1, next - prev))));
        var width = ((1 - share) * 100).toFixed(1) + '%';
        if (fill.style.width !== width) fill.style.width = width;
      }
      if (box.getAttribute('data-live') !== 'true') box.setAttribute('data-live', 'true');
    }

    try {
      tick();
    } catch (error) {
      return;
    }
    this.urgencyTimer = window.setInterval(tick, 15000);
    this.urgencyVisible = function () { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', this.urgencyVisible);
  };

  Pdp.prototype.destroy = function () {
    this.stopGlide();
    if (this.trackObserver) this.trackObserver.disconnect();
    if (this.urgencyTimer) window.clearInterval(this.urgencyTimer);
    if (this.urgencyVisible) document.removeEventListener('visibilitychange', this.urgencyVisible);
    if (this.observer) this.observer.disconnect();
    if (this.stickyObserver) this.stickyObserver.disconnect();
    if (this.placeSticky) window.removeEventListener('resize', this.placeSticky);
    window.clearTimeout(this.resetTimer);
  };

  /* ---------- boot ----------------------------------------------------- */

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var found = slice(context.querySelectorAll(ROOT));
    if (context.matches && context.matches(ROOT)) found.unshift(context);

    found.forEach(function (root) {
      if (root.getAttribute('data-pdp-ready') === 'true') return;
      root.setAttribute('data-pdp-ready', 'true');
      try {
        root.fndryPdp = new Pdp(root);
      } catch (error) {
        /* Put the gallery back to the stacked, everything-visible state rather
           than leaving one slide showing and the rest hidden. */
        var gallery = root.querySelector('[data-fpb-gallery]');
        if (gallery) gallery.removeAttribute('data-fpb-ready');
        slice(root.querySelectorAll('[data-fpb-slide]')).forEach(function (slide) {
          slide.removeAttribute('hidden');
        });
        if (window.console && console.error) console.error('[fndry-pdp-buy]', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    var root = target.matches && target.matches(ROOT) ? target : target.querySelector(ROOT);
    if (!root) return;
    if (root.fndryPdp && root.fndryPdp.destroy) root.fndryPdp.destroy();
    root.removeAttribute('data-pdp-ready');
  });

  /* Selecting a media block isn't possible here — media lives on the product,
     not in blocks — but selecting the section should still leave the gallery
     on its first frame rather than wherever the editor last left it. */
  document.addEventListener('shopify:section:select', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    var root = target.matches && target.matches(ROOT) ? target : target.querySelector(ROOT);
    if (root && root.fndryPdp) root.fndryPdp.show(1, false);
  });
})();
